import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { projects as projectsApi, ai as aiApi } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../lib/theme";
import { ensureProjectDefaults, computeProgress, describePatch, applyProjectSkin, debtKanbanCards, worldTilesetPng } from "../lib/gamecraft";
import { ProgressRing, AIAssistantBar } from "../components/gc/ui";
import {
  House, Folder, Lightbulb, BookOpen, Palette, MusicNotes, Code,
  Graph, Cpu, Gear, MagnifyingGlass, Bell, SignOut, Sun, Moon,
  GameController, List, CaretDown, NotePencil, ArrowUUpLeft, ArrowUUpRight, Play,
} from "@phosphor-icons/react";
import OverviewPanel from "../components/ide/OverviewPanel";
import ProjectPanel from "../components/ide/ProjectPanel";
import IdeasPanel from "../components/ide/IdeasPanel";
import StoryPanel from "../components/ide/StoryPanel";
import SpriteHub from "../components/ide/SpriteHub";
import AudioHub from "../components/ide/AudioHub";
import CodeHub from "../components/ide/CodeHub";
import CanvasKanbanPanel from "../components/ide/CanvasKanbanPanel";
import EngineHub from "../components/ide/EngineHub";
import AIPanel from "../components/ide/AIPanel";
import SettingsPanel from "../components/ide/SettingsPanel";
import NotesPanel from "../components/ide/NotesPanel";
import ShareDialog from "../components/ide/ShareDialog";
import CommandPalette from "../components/ide/CommandPalette";
import { Sheet, SheetContent, SheetTitle } from "../components/ui/sheet";
import { toast } from "sonner";
import ProfileAvatar from "../components/gc/ProfileAvatar";
import HelpGuide, { HelpButton } from "../components/gc/HelpGuide";
import { firstName, displayHandle } from "../lib/profile";
import StudioRail from "../components/gc/StudioRail";
import { studioHealth } from "../lib/studioHealth";

const TABS = [
  { key: "overview", icon: House, i18n: "ide.tabs.overview" },
  { key: "project", icon: Folder, i18n: "ide.tabs.project" },
  { key: "ideas", icon: Lightbulb, i18n: "ide.tabs.ideas" },
  { key: "story", icon: BookOpen, i18n: "ide.tabs.story" },
  { key: "sprites", icon: Palette, i18n: "ide.tabs.sprites" },
  { key: "audio", icon: MusicNotes, i18n: "ide.tabs.audio" },
  { key: "code", icon: Code, i18n: "ide.tabs.code" },
  { key: "canvas", icon: Graph, i18n: "ide.tabs.canvas" },
  { key: "engine", icon: Cpu, i18n: "ide.tabs.engine" },
  { key: "notes", icon: NotePencil, i18n: "Notas" },
  { key: "settings", icon: Gear, i18n: "ide.tabs.settings" },
];

const MOBILE_TABS = ["overview", "ideas", "sprites", "engine", "notes"];

function Nav({ tab, setTab, collapsed }) {
  const { t } = useI18n();
  return (
    <nav className={"flex-1 overflow-y-auto gf-scroll py-2 " + (collapsed ? "px-1" : "px-2")}>
      {TABS.map((x) => {
        const Icon = x.icon;
        const active = tab === x.key;
        return (
          <button
            key={x.key}
            data-testid={`ide-tab-${x.key}`}
            onClick={() => setTab(x.key)}
            title={typeof x.i18n === "string" && x.i18n.startsWith("ide.") ? t(x.i18n) : x.i18n}
            className={"gc-nav-item mb-0.5 " + (active ? "active" : "") + (collapsed ? " justify-center px-0" : "")}
          >
            <Icon size={18} weight={active ? "fill" : "regular"} className="shrink-0" />
            {!collapsed && <span className="truncate">{typeof x.i18n === "string" && x.i18n.startsWith("ide.") ? t(x.i18n) : x.i18n}</span>}
          </button>
        );
      })}
    </nav>
  );
}

export default function ProjectIDE() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { t, lang, setLang } = useI18n();
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTabState] = useState("overview");
  const [saving, setSaving] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [cmdkOpen, setCmdkOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiOut, setAiOut] = useState("");
  const [aiOpen, setAiOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const saveTimer = useRef(null);
  const historyRef = useRef([]);
  const futureRef = useRef([]);
  const [histTick, setHistTick] = useState(0);
  const [focus, setFocus] = useState(null);

  const structuralSnap = (p) => JSON.parse(JSON.stringify({
    name: p.name,
    description: p.description,
    genre: p.genre,
    engine: p.engine,
    entities: p.entities,
    story_items: p.story_items,
    notes: p.notes,
    kanban: p.kanban,
    gdd: p.gdd,
    config: p.config,
    idea_modules: p.idea_modules,
    files: p.files,
    diagram: p.diagram,
    tags: p.tags,
    soundboard: p.soundboard,
  }));

  const setTab = useCallback((k) => { setTabState(k); setMobileNavOpen(false); }, []);
  const jumpTo = useCallback((k, extra) => {
    if (extra && typeof extra === "object") setFocus(extra);
    setTab(k);
  }, [setTab]);

  useEffect(() => {
    (async () => {
      try {
        const p = await projectsApi.get(projectId);
        const hydrated = ensureProjectDefaults(p);
        setProject(hydrated);
        if (!p.config?.starter_kit && hydrated.config?.starter_kit) {
          persist({
            sprites: hydrated.sprites,
            entities: hydrated.entities,
            story_items: hydrated.story_items,
            kanban: hydrated.kanban,
            soundboard: hydrated.soundboard,
            gdd: hydrated.gdd,
            diagram: hydrated.diagram,
            animations: hydrated.animations,
            tags: hydrated.tags,
            config: hydrated.config,
          });
        }
        const list = await projectsApi.list().catch(() => []);
        setProjects(list);
      } catch {
        toast.error(t("toast.notFound"));
        navigate("/dashboard", { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [projectId, navigate]); // eslint-disable-line

  const persist = useCallback(async (patch) => {
    setSaving(true);
    try {
      const updated = await projectsApi.update(projectId, patch);
      setProject(ensureProjectDefaults(updated));
    } catch {
      toast.error(t("toast.saveFailed"));
    } finally {
      setSaving(false);
    }
  }, [projectId]); // eslint-disable-line

  const updateProject = useCallback((patch) => {
    setProject((p) => {
      historyRef.current.push(structuralSnap(p));
      if (historyRef.current.length > 30) historyRef.current.shift();
      futureRef.current = [];
      const next = ensureProjectDefaults({ ...p, ...patch });
      const act = {
        ts: new Date().toISOString(),
        message: describePatch(patch),
        color: "blue",
      };
      next.activity = [...(next.activity || []).slice(-40), act];
      return next;
    });
    setHistTick((n) => n + 1);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persist({ ...patch }), 700);
  }, [persist]);

  const undoProject = useCallback(() => {
    const prev = historyRef.current.pop();
    if (!prev) return;
    setProject((cur) => {
      futureRef.current.push(structuralSnap(cur));
      return ensureProjectDefaults({ ...cur, ...prev });
    });
    setHistTick((n) => n + 1);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    persist(prev);
    toast.message("Deshecho");
  }, [persist]);

  const redoProject = useCallback(() => {
    const nxt = futureRef.current.pop();
    if (!nxt) return;
    setProject((cur) => {
      historyRef.current.push(structuralSnap(cur));
      return ensureProjectDefaults({ ...cur, ...nxt });
    });
    setHistTick((n) => n + 1);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    persist(nxt);
    toast.message("Rehecho");
  }, [persist]);

  useEffect(() => {
    const onKey = (e) => {
      if (helpOpen || cmdkOpen || shareOpen || aiOpen) return;
      const tag = (e.target?.tagName || "").toUpperCase();
      const inField = ["INPUT", "TEXTAREA", "SELECT"].includes(tag) || e.target?.isContentEditable;
      const inMonaco = !!e.target?.closest?.(".monaco-editor");
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z" && !e.shiftKey && !inField && !inMonaco) {
        e.preventDefault();
        undoProject();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === "y" || (e.shiftKey && e.key.toLowerCase() === "z")) && !inField && !inMonaco) {
        e.preventDefault();
        redoProject();
        return;
      }
      if (e.altKey && e.key >= "1" && e.key <= "9") {
        e.preventDefault();
        const idx = Number(e.key) - 1;
        if (TABS[idx]) setTab(TABS[idx].key);
        return;
      }
      if (e.altKey && e.key === "0") {
        e.preventDefault();
        setTab("notes");
        return;
      }
      if (e.key === "?" && !inField && !inMonaco) {
        e.preventDefault();
        setHelpOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [helpOpen, cmdkOpen, shareOpen, aiOpen, undoProject, redoProject, setTab]);

  useEffect(() => {
    applyProjectSkin(project?.config);
    return () => applyProjectSkin(null);
  }, [
    project?.config?.skin,
    project?.config?.accent,
    project?.config?.bg,
    project?.config?.banner,
    project?.config?.bg_image,
    project?.config?.bg_preset,
    project?.config?.bg_source,
    project?.config?.pattern,
    project?.config?.glow,
    project?.config?.accent2,
    project?.config?.tool_bg,
    project?.config?.theme_apply,
    JSON.stringify(project?.config?.theme_apply || {}),
    theme,
  ]);

  const handleAi = async (prompt, action) => {
    if (action === "guide") {
      setHelpOpen(true);
      return;
    }
    if (action === "engine") {
      setTab("engine");
      toast.success("Run en el motor. Cámara: " + (project.config?.camera === "topdown" ? "arriba" : "lado"));
      return;
    }
    if (action === "playlog") {
      const note = window.prompt("Nota de playtest (qué se rompió / qué se sintió / qué cortar)");
      if (!note) return;
      updateProject({ config: { ...(project.config || {}), playtest_log: [...(project.config?.playtest_log || []), { ts: new Date().toISOString(), note }] } });
      setTab("overview");
      toast.success("Nota guardada");
      return;
    }
    if (action === "debt") {
      const cards = debtKanbanCards(project);
      const columns = (project.kanban?.columns || []).map((c) => c.id === "todo" ? { ...c, cards: [...(c.cards || []), ...cards] } : c);
      updateProject({ kanban: { ...(project.kanban || {}), columns } });
      setTab("canvas");
      toast.success(`${cards.length} tarjetas de sprites faltantes`);
      return;
    }
    if (action === "tileset") {
      const skin = project.config?.skin || "forest";
      const name = `mundo_${skin}`;
      if ((project.sprites || []).some((s) => s.name === name)) {
        setTab("sprites");
        toast.message("Ya existe el tileset del mundo");
        return;
      }
      try {
        await projectsApi.addSprite(project.project_id, {
          name, data_url: worldTilesetPng(skin), category: "tileset", studio: "world", width: 16, height: 16,
        });
        const p = await projectsApi.get(project.project_id);
        updateProject({ sprites: p.sprites });
        setTab("sprites");
        toast.success("Tileset del mundo en biblioteca");
      } catch {
        toast.error("No se pudo guardar el tileset");
      }
      return;
    }
    setAiLoading(true);
    try {
      const wantsSprite = action === "sprite" || tab === "sprites" || /sprite|pixel art|genera(r|me)? (un |una )?(sprite|imagen)/i.test(prompt || "");
      if (wantsSprite) {
        const desc = (!prompt || /generar sprite/i.test(prompt)) && (prompt || "").trim().length < 24
          ? `${project.name} hero, pixel art character`
          : prompt;
        const cam = project.config?.camera === "topdown" ? "top-down Gungeon" : "side Metal Slug";
        const size = 32;
        const res = await aiApi.sprite({
          prompt: `pixel art 2D game sprite, ${cam}, transparent background, no text. ${desc}`,
          style: "pixel art",
          size,
          provider: user?.ai_provider || "auto",
        });
        const raw = res.data || "";
        const url = raw.startsWith("data:") ? raw : `data:${res.mime_type || "image/png"};base64,${raw}`;
        if (res.needs_api_key) {
          toast.error("Añade Gemini, Claude o ChatGPT en Ajustes para generar sprites.");
          setTab("settings");
          return;
        }
        await projectsApi.addSprite(project.project_id, {
          name: (prompt || "ai-sprite").slice(0, 40),
          data_url: url,
          category: "character",
          studio: "creatures",
        });
        const p = await projectsApi.get(project.project_id);
        updateProject({ sprites: p.sprites });
        setTab("sprites");
        toast.success(
          res.source === "gemini" ? "Sprite generado e importado"
            : res.source === "pollinations" ? "Respaldo de imagen importado (no Gemini)"
            : "Plantilla procedural (ningún motor de imagen respondió)"
        );
        return;
      }
      const res = await aiApi.ideas({
        prompt: `[${tab}] ${prompt}\nJuego: ${project.name}. Idea: ${project.gdd?.idea || ""}`,
        category: tab,
        language: lang,
        provider: user?.ai_provider || "auto",
      });
      setAiOut(res.text || "");
      setAiOpen(true);
      toast.success("Respuesta de IA lista");
    } catch {
      toast.error(t("toast.aiFailed"));
    } finally {
      setAiLoading(false);
    }
  };

  if (loading || !project) {
    return (
      <div className="gc-app flex items-center justify-center min-h-screen text-sm" style={{ color: "var(--gc-muted)" }}>
        <span className="gf-blink">Cargando GameCraft 2D…</span>
      </div>
    );
  }

  const progress = computeProgress(project);
  const healthNow = studioHealth(project);

  const renderTab = () => {
    const common = { project, onChange: updateProject, onJump: jumpTo, focus };
    switch (tab) {
      case "overview": return <OverviewPanel {...common} onHelp={() => setHelpOpen(true)} />;
      case "project": return <ProjectPanel {...common} />;
      case "ideas": return <IdeasPanel {...common} />;
      case "story": return <StoryPanel {...common} />;
      case "sprites": return <SpriteHub {...common} />;
      case "audio": return <AudioHub {...common} />;
      case "code": return <CodeHub {...common} />;
      case "canvas": return <CanvasKanbanPanel {...common} />;
      case "engine": return <EngineHub {...common} />;
      case "notes": return <NotesPanel {...common} />;
      case "settings": return <SettingsPanel {...common} />;
      default: return null;
    }
  };

  const sidebar = (collapsed) => (
    <>
      <div className={"flex items-center gap-2 py-4 " + (collapsed ? "px-2 justify-center" : "px-4")}>
        <div className="w-9 h-9 rounded-xl bg-sky-500 text-white flex items-center justify-center shrink-0">
          <GameController size={18} weight="fill" />
        </div>
        {!collapsed && <div className="font-display font-bold leading-tight">GameCraft<br /><span className="text-sky-500 text-xs">2D</span></div>}
      </div>
      <Nav tab={tab} setTab={setTab} collapsed={collapsed} />
      {!collapsed && (
        <div className="px-4 py-3 flex flex-col items-center">
          <ProgressRing value={healthNow.overall} size={84} label="Salud" />
          <div className="text-[10px] mt-1" style={{ color: "var(--gc-muted)" }}>Progreso {progress}%</div>
        </div>
      )}
      {!collapsed && (
        <button
          type="button"
          onClick={() => setTab("settings")}
          className="px-4 py-3 flex items-center gap-2 border-t text-left w-full"
          style={{ borderColor: "var(--gc-border)" }}
        >
          <ProfileAvatar user={user} size={36} />
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate">{user?.name || "Creador"}</div>
            <div className="text-[11px] truncate" style={{ color: "var(--gc-muted)" }}>
              {user?.studio || displayHandle(user) || "Editar perfil"}
            </div>
          </div>
        </button>
      )}
      <div className={"py-3 flex items-center gap-2 border-t " + (collapsed ? "justify-center px-1" : "px-4")} style={{ borderColor: "var(--gc-border)" }}>
        <button onClick={toggle} className="gc-btn gc-btn-ghost h-9 w-9 p-0" title="Tema">
          {theme === "light" ? <Moon size={16} /> : <Sun size={16} />}
        </button>
        {!collapsed && (
          <button onClick={async () => { await logout(); navigate("/"); }} className="gc-btn gc-btn-ghost h-9 w-9 p-0 ml-auto text-red-400">
            <SignOut size={16} />
          </button>
        )}
      </div>
    </>
  );

  return (
    <div className="h-[100dvh] flex flex-col gc-app overflow-hidden">
      <header className="gc-glass mx-2 sm:mx-3 mt-2 sm:mt-3 px-2 sm:px-4 py-2 flex items-center gap-2 z-30" style={{ borderRadius: 20 }}>
        <button type="button" className="md:hidden gc-btn gc-btn-ghost h-10 w-10 p-0" onClick={() => setMobileNavOpen(true)} aria-label="Menú"><List size={18} /></button>
        <button type="button" onClick={() => navigate("/dashboard")} className="hidden md:flex gc-btn gc-btn-ghost h-10 px-3 text-xs">Proyectos</button>
        <div className="relative min-w-0 flex-1 md:flex-none">
          <select
            className="gc-input h-10 pl-3 pr-8 text-sm font-semibold appearance-none w-full md:min-w-[180px] md:w-auto"
            value={project.project_id}
            onChange={(e) => navigate(`/project/${e.target.value}`)}
          >
            {projects.length ? projects.map((p) => <option key={p.project_id} value={p.project_id}>{p.name}</option>) : <option value={project.project_id}>{project.name}</option>}
          </select>
          <CaretDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
        <button type="button" onClick={() => setCmdkOpen(true)} className="hidden sm:flex flex-1 max-w-xl mx-auto items-center gap-2 gc-input h-10 px-4 text-sm" style={{ color: "var(--gc-muted)" }}>
          <MagnifyingGlass size={14} /> Buscar en el proyecto
          <kbd className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-black/5">Ctrl + K</kbd>
        </button>
        <div className="flex items-center gap-0.5 sm:gap-1 shrink-0">
          <button
            type="button"
            className="hidden sm:inline-flex gc-btn gc-btn-ghost h-10 w-10 p-0"
            title="Deshacer (Ctrl+Z)"
            disabled={histTick >= 0 && !historyRef.current.length}
            onClick={undoProject}
          >
            <ArrowUUpLeft size={16} />
          </button>
          <button
            type="button"
            className="hidden sm:inline-flex gc-btn gc-btn-ghost h-10 w-10 p-0"
            title="Rehacer (Ctrl+Y)"
            disabled={histTick >= 0 && !futureRef.current.length}
            onClick={redoProject}
          >
            <ArrowUUpRight size={16} />
          </button>
          <HelpButton onClick={() => setHelpOpen(true)} />
          <button type="button" onClick={() => setCmdkOpen(true)} className="sm:hidden gc-btn gc-btn-ghost h-10 w-10 p-0" aria-label="Buscar"><MagnifyingGlass size={16} /></button>
          <span className="hidden md:inline text-[11px] px-1" style={{ color: "var(--gc-muted)" }}>{saving ? "Guardando…" : "Guardado"}</span>
          <button type="button" onClick={toggle} className="hidden sm:inline-flex gc-btn gc-btn-ghost h-10 w-10 p-0">{theme === "light" ? <Sun size={16} /> : <Moon size={16} />}</button>
          <div className="relative">
            <button type="button" className="gc-btn gc-btn-ghost h-10 w-10 p-0" onClick={() => setBellOpen((v) => !v)} title="Actividad">
              <Bell size={16} />
              {(project.activity || []).length > 0 && <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-sky-500" />}
            </button>
            {bellOpen && (
              <div className="absolute right-0 top-11 w-[min(18rem,calc(100vw-1.5rem))] gc-card p-3 z-50 max-h-80 overflow-auto">
                <div className="text-xs font-semibold mb-2">Actividad</div>
                {(project.activity || []).slice(-8).reverse().map((a, i) => (
                  <div key={i} className="text-xs py-1" style={{ color: "var(--gc-muted)" }}>{a.message}</div>
                ))}
                {!(project.activity || []).length && <p className="text-xs" style={{ color: "var(--gc-muted)" }}>Sin actividad aún.</p>}
                {(project.config?.playtest_log || []).slice(-3).reverse().map((x, i) => (
                  <div key={`p${i}`} className="text-xs py-1 border-t mt-1 pt-1" style={{ borderColor: "var(--gc-border)", color: "var(--gc-muted)" }}>
                    Playtest: {x.note}
                  </div>
                ))}
              </div>
            )}
          </div>
          <button type="button" onClick={() => setLang(lang === "es" ? "en" : "es")} className="hidden sm:inline-flex gc-btn gc-btn-ghost h-10 px-2 text-xs">{lang.toUpperCase()}</button>
          <button
            type="button"
            onClick={() => setTab("settings")}
            className="flex items-center gap-2 pl-1 pr-1 sm:pr-2 h-10 rounded-full hover:bg-black/5"
            title="Tu perfil"
          >
            <ProfileAvatar user={user} size={32} />
            <span className="hidden lg:block text-left leading-tight">
              <span className="block text-xs font-semibold">{firstName(user)}</span>
              {user?.studio && <span className="block text-[10px]" style={{ color: "var(--gc-muted)" }}>{user.studio}</span>}
            </span>
          </button>
        </div>
      </header>

      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} project={project} onProjectChange={(patch) => setProject((p) => ({ ...p, ...patch }))} />
      <CommandPalette open={cmdkOpen} onOpenChange={setCmdkOpen} tabs={TABS} project={project} onJump={(k) => { if (k === "ai") setAiOpen(true); else jumpTo(k); }} onShare={() => setShareOpen(true)} />
      <HelpGuide open={helpOpen} onOpenChange={setHelpOpen} project={project} onJump={jumpTo} />

      <div className="flex-1 flex overflow-hidden px-2 sm:px-3 pb-[4.75rem] md:pb-3 pt-2 sm:pt-3 gap-3 min-h-0">
        <aside className="hidden md:flex gc-glass w-56 flex-col overflow-hidden" style={{ borderRadius: 24 }}>
          {sidebar(false)}
        </aside>
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <SheetContent side="left" className="p-0 w-[min(18rem,90vw)]" style={{ background: "var(--gc-sidebar)" }}>
            <SheetTitle className="sr-only">Navegación</SheetTitle>
            <div className="flex flex-col h-full">{sidebar(false)}</div>
          </SheetContent>
        </Sheet>

        <div className="flex-1 flex flex-col min-w-0 gap-2 sm:gap-3">
          <StudioRail
            project={project}
            tab={tab}
            focus={focus}
            onJump={jumpTo}
            onRun={() => jumpTo("engine")}
            onAi={() => setAiOpen(true)}
          />
          <main className="flex-1 overflow-auto gf-scroll gc-glass min-h-0" style={{ borderRadius: 24 }}>
            {renderTab()}
          </main>
          <div className="hidden md:block">
            <AIAssistantBar onSend={handleAi} loading={aiLoading} />
          </div>
        </div>
      </div>

      <button
        type="button"
        className={"gc-fab-run " + (tab === "engine" ? "is-engine" : "")}
        onClick={() => jumpTo("engine")}
        aria-label="Probar en motor"
      >
        <Play size={22} weight="fill" />
      </button>

      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 gc-glass px-1 pt-1 pb-[max(0.4rem,env(safe-area-inset-bottom))] flex items-stretch justify-around" style={{ borderRadius: "18px 18px 0 0" }}>
        {MOBILE_TABS.map((key) => {
          const x = TABS.find((t) => t.key === key);
          if (!x) return null;
          const Icon = x.icon;
          const active = tab === key;
          const label = typeof x.i18n === "string" && x.i18n.startsWith("ide.") ? t(x.i18n) : x.i18n;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={"flex flex-col items-center justify-center gap-0.5 min-h-[52px] flex-1 text-[10px] " + (active ? "text-sky-500" : "")}
              style={{ color: active ? "var(--gc-primary)" : "var(--gc-muted)" }}
            >
              <Icon size={20} weight={active ? "fill" : "regular"} />
              <span className="truncate max-w-[4.5rem]">{label}</span>
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => setMobileNavOpen(true)}
          className="flex flex-col items-center justify-center gap-0.5 min-h-[52px] flex-1 text-[10px]"
          style={{ color: "var(--gc-muted)" }}
        >
          <List size={20} />
          <span>Más</span>
        </button>
      </nav>

      <Sheet open={aiOpen} onOpenChange={setAiOpen}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-auto" style={{ background: "var(--gc-sidebar)" }}>
          <SheetTitle>Asistente IA</SheetTitle>
          {aiOut && <div className="gc-card p-4 mt-3 whitespace-pre-wrap text-sm">{aiOut}</div>}
          <div className="mt-4"><AIPanel project={project} /></div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
